// src/AgentSearchWidget.js

import widgetTemplate from './as-widget.html';
import widgetCSS from '!!raw-loader!./as-widget.css';
import agentTemplate from './agent.html';
import agentsTemplate from './agents.html';
import errorTemplate from '../shared/error.html';
import * as helpers from '../shared/helpers';
import { bootstrapStyle } from '../shared/styles';
import { library, dom } from '@fortawesome/fontawesome-svg-core'
import { all } from '@awesome.me/kit-bd5fe14630/icons'

const combinedStyles = `${widgetCSS}\n${bootstrapStyle}`

class AgentSearchWidget extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        const style = document.createElement('style');
        style.textContent = combinedStyles;
        this.shadowRoot.appendChild(style);

        const template = document.createElement('section');
        template.classList.add('ha-widget');
        template.innerHTML = widgetTemplate;
        this.shadowRoot.appendChild(template);

        library.add(...all);
        dom.watch({
            autoReplaceSvgRoot: this.shadowRoot,
            observeMutationsRoot: this.shadowRoot
        });
    }

    connectedCallback() {
        this.baseUrl = helpers.getBaseUrl();
        this.baseApiUrl = helpers.getBaseApiUrl();
        this.primaryColor = this.getAttribute('primary-color') || '#024267';
        this.secondaryColor = this.getAttribute('secondary-color') || '#F79322';
        this.widgetBackground = this.getAttribute('background-color') || 'transparent';
        this.fontFamily = this.getAttribute('font-family') || '"Helvetica Neue", Helvetica, Arial, sans-serif'
        this.fontUrl = this.getAttribute('font-url') || '';
        this.showOverlay = this.getAttribute('show-overlay') || 'no';
        this.primaryErrorText = this.getAttribute('primary-error-text') || 'No automated valuation could be generated for:';
        this.secondaryErrorText = this.getAttribute('secondary-error-text') || 'Want a professional estimate from an agent in the area?';
        this.errorPhone = this.getAttribute('error-phone') || null;
        this.errorEmail = this.getAttribute('error-email') || 'service@homeadvantage.com';
        this.init();
    }

    init() {
        helpers.setColors(this);
        this.setupSearchInput();
    }

    setupSearchInput() {
        const searchInput = this.shadowRoot.getElementById('search-input');
        const searchGroup = this.shadowRoot.getElementById('wrapper');
        const showOverlay = this.showOverlay.toLowerCase() === 'yes';

        if (showOverlay) {
            const overlay = this.shadowRoot.querySelector('.ha-overlay');
            overlay.style.width = '100%';
            overlay.style.padding = '4em';
            overlay.style.backgroundColor = this.getAttribute('overlay-background') || this.primaryColor;

            const overlayHeading = this.getAttribute('overlay-heading');
            if (overlayHeading) {
                const heading = document.createElement('h1');
                heading.classList.add('text-white');
                heading.innerHTML = overlayHeading;
                searchGroup.parentElement.insertBefore(heading, searchGroup);
            }

            const overlaySubHeading = this.getAttribute('overlay-sub-heading');
            if (overlaySubHeading) {
                const subHeading = document.createElement('h6');
                subHeading.classList.add('text-white');;
                subHeading.innerHTML = overlaySubHeading;
                searchGroup.parentElement.insertBefore(subHeading, searchGroup);
            }
        }

        const placeholder = this.getAttribute('text-placeholder') || 'Search by City, Zip, Neighborhood...';
        searchInput.setAttribute('placeholder', placeholder);

        let timeoutId;
        let abortController = new AbortController();

        searchInput.addEventListener('input', (e) => {
            const inputValue = e.target.value.toLowerCase();
            helpers.closeAllLists(this);

            if (inputValue.length < 3) {
                return false;
            }
            const spinner = this.shadowRoot.querySelector('.searching');
            spinner.classList.remove('d-none');

            if (abortController) {
                abortController.abort();
            }
            abortController = new AbortController();

            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                this.getArea(inputValue, abortController.signal, searchInput);
            }, 300);
        });
    }

    async getArea(text, signal, searchInput) {
        const endpoint = 'api/services/app/HomeSearch/GetSearchArea';
        const queryParams = { input: text };
        const url = `${this.baseApiUrl}${endpoint}?${helpers.serializeParams(queryParams)}`;

        try {
            const response = await fetch(url, { signal, headers: helpers.getHeaders() });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();
            const autocompleteItems = this.shadowRoot.getElementById('autocomplete-items');
            autocompleteItems.style.display = 'block';

            if (data.result.areas.length > 0) {
                data.result.areas.forEach(suggestion => {
                    const listItem = document.createElement('li');
                    listItem.innerHTML = suggestion.label;
                    listItem.addEventListener('click', () => {
                        searchInput.value = suggestion.label;
                        helpers.closeAllLists(this);
                        this.getAgents(suggestion);
                    });
                    autocompleteItems.appendChild(listItem);
                });
            }
            else {
                const listItem = document.createElement('li');
                listItem.innerHTML = 'No results found';
                autocompleteItems.appendChild(listItem);
            }

            const spinner = this.shadowRoot.querySelector('.searching');
            spinner.classList.add('d-none');
        } catch (error) {
            console.error('Fetch error:', error);
        }
    }

    async getAgents(search) {
        var hasError = false;

        const errorContainer = this.shadowRoot.querySelector('.error-container');
        errorContainer.classList.add('d-none');
        errorContainer.innerHTML = errorTemplate;

        const loading = this.shadowRoot.querySelector('.loading-overlay').parentElement;
        loading.classList.remove('d-none');
        loading.scrollIntoView({ behavior: 'smooth' });

        const agentsContainer = this.shadowRoot.querySelector(".agent-container");
        agentsContainer.classList.add('d-none');
        agentsContainer.innerHTML = agentsTemplate;

        try {
            const agentsDataEndpoint = 'api/services/app/AgentMasters/GetAllByZipCodePublic';
            const agentsQueryParams = { areaIds: search.id, maxResultCount: 3, skipCount: 0 };
            const agentsUrl = `${this.baseApiUrl}${agentsDataEndpoint}?${helpers.serializeParams(agentsQueryParams)}`;

            const agentsResponse = await fetch(agentsUrl, { headers: helpers.getHeaders() });
            if (!agentsResponse.ok) {
                throw new Error('Network response was not ok');
            }


            const agentsData = await agentsResponse.json();
            agentsContainer.querySelector('.agent-count').innerHTML = agentsData.result.totalCount;
            agentsData.result.items.forEach(data => {
                const agentContainer = document.createElement('div');
                agentContainer.classList.add('col');
                agentContainer.innerHTML = agentTemplate;

                if (!data.pictureUrl) { data.pictureUrl = "Agent Photos/no-image.jpg"; }
                agentContainer.querySelector('img').src = 'https://mycuha.blob.core.windows.net/mycuha/' + data.pictureUrl;
                agentContainer.querySelector('.card-title').innerHTML = data.firstName + ' ' + data.lastName;
                agentContainer.querySelector('.agent-name').innerHTML = data.firstName;
                const agentBio = agentContainer.querySelector('.agent-bio');
                agentBio.innerHTML = data.bioFormatted + '<span class="show-more"><span class="text-primary">Show More</span></span>';
                const showMoreLink = agentBio.querySelector('.show-more .text-primary');
                showMoreLink.addEventListener('touchstart', () => {
                    agentBio.classList.add('expanded');
                });

                document.addEventListener('touchstart', (e) => {
                    // Check if the tap was outside `agentBio` when it’s expanded
                    if (!agentBio.contains(e.target) && agentBio.classList.contains('expanded')) {
                        agentBio.classList.remove('expanded');
                    }
                });

                showMoreLink.addEventListener('mouseover', () => {
                    agentBio.classList.add('expanded');
                    showMoreLink.classList.add('d-none');
                });

                agentBio.addEventListener('mouseleave', () => {
                    agentBio.classList.remove('expanded');
                    showMoreLink.classList.remove('d-none');
                });

                agentsContainer.querySelector('.agent-data').appendChild(agentContainer);
            });
            console.log(agentsData);

        } catch (error) {
            hasError = true;
            console.error('Fetch error:', error);
        }

        if (hasError) {
            errorContainer.querySelector('.error-text').innerHTML = 'Could not locate any agents in ' + search.label;
            errorContainer.querySelector('.secondary-text').innerHTML = 'Don\'t worry, we can still help you find a great agent!';
            errorContainer.classList.remove('d-none');
            errorContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        else {
            agentsContainer.classList.remove('d-none');
            agentsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        loading.classList.add('d-none');
    }
}

if (!customElements.get('agent-search-widget')) {
    customElements.define('agent-search-widget', AgentSearchWidget);
}

export default AgentSearchWidget;